import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="attachment-type-duplicator"
export default class extends Controller {
  static targets = [
    "sourceAttachmentType",
    "sourceMonth",
    "targetMonth",
    "targetYear",
    "newName"
  ]

  // Hebrew month names (indexed from 1-12)
  static hebrewMonths = [
    null, // 0 - placeholder
    "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
    "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
  ]

  // Activity months in order (Sept-June: 9,10,11,12,1,2,3,4,5,6)
  static activityMonths = [9, 10, 11, 12, 1, 2, 3, 4, 5, 6]

  connect() {
    logger.log("AttachmentTypeDuplicator controller connected")

    // Set default target year to current year if available
    this.setDefaultYear()
  }

  // When source attachment type is selected
  sourceAttachmentTypeChanged() {
    const selectedOption = this.sourceAttachmentTypeTarget.selectedOptions[0]
    if (!selectedOption || !selectedOption.value) return

    const attachmentTypeName = selectedOption.text

    // Auto-select target year to match source attachment type's year
    // The options are grouped by year, so we can extract it from the optgroup
    const optgroup = selectedOption.parentElement
    if (optgroup && optgroup.tagName === 'OPTGROUP') {
      const sourceYearName = optgroup.label

      // Find and select the matching year in target year dropdown
      for (let i = 0; i < this.targetYearTarget.options.length; i++) {
        if (this.targetYearTarget.options[i].text === sourceYearName) {
          this.targetYearTarget.selectedIndex = i
          break
        }
      }
    }

    // Try to detect source month from name
    const detectedMonth = this.detectMonthFromName(attachmentTypeName)

    if (detectedMonth) {
      logger.log(`Detected month ${detectedMonth} from name: ${attachmentTypeName}`)
      this.sourceMonthTarget.value = detectedMonth
      this.sourceMonthChanged() // Trigger cascade
    } else {
      logger.log(`Could not detect month from name: ${attachmentTypeName}`)
    }
  }

  // When source month is changed
  sourceMonthChanged() {
    const sourceMonth = parseInt(this.sourceMonthTarget.value)

    if (sourceMonth) {
      // Set target month to next month in activity sequence
      const nextMonth = this.getNextActivityMonth(sourceMonth)
      this.targetMonthTarget.value = nextMonth
    }

    this.updateNewName()
  }

  // When target month is changed
  targetMonthChanged() {
    this.updateNewName()
  }

  // When target year is changed
  targetYearChanged() {
    this.updateNewName()
  }

  // Detect month from attachment type name (Hebrew)
  detectMonthFromName(name) {
    // Try to find Hebrew month name in name
    for (let month = 1; month <= 12; month++) {
      const hebrewName = this.constructor.hebrewMonths[month]
      if (name.includes(hebrewName)) {
        return month
      }
    }
    return null
  }

  // Get next month in activity sequence
  getNextActivityMonth(currentMonth) {
    const currentIndex = this.constructor.activityMonths.indexOf(currentMonth)

    if (currentIndex === -1) {
      // Not found, default to September
      return 9
    }

    // Get next month, wrap around to September if we're at June
    const nextIndex = (currentIndex + 1) % this.constructor.activityMonths.length
    return this.constructor.activityMonths[nextIndex]
  }

  // Get Hebrew month name for a month number
  getHebrewMonthName(month) {
    return this.constructor.hebrewMonths[month] || ""
  }

  // Get calendar year for a month in an academic year
  // Uses activity month order: months in first half (Sep-Dec) use start year,
  // months in second half (Jan-Jun) use start year + 1
  // This matches the Ruby logic in AttachmentProcessor::MonthlyReport
  getCalendarYearForMonth(month, academicStartYear) {
    const activityMonths = this.constructor.activityMonths
    const breakoffIndex = 4 // After December (index 3), months use next calendar year

    const index = activityMonths.indexOf(month)
    if (index === -1) {
      // Month not in activity year - fall back to simple logic
      return month >= 9 ? academicStartYear : academicStartYear + 1
    }

    // First half of activity year (Sep-Dec, indices 0-3) → start year
    // Second half (Jan-Jun, indices 4-9) → start year + 1
    return index < breakoffIndex ? academicStartYear : academicStartYear + 1
  }

  // Extract academic start year from year option's data attribute
  // Falls back to parsing text if data attribute is not available
  getAcademicStartYear(yearOption) {
    // First try to get from data attribute (preferred)
    const startYear = yearOption.dataset.startYear
    if (startYear) {
      return parseInt(startYear)
    }

    // Fallback: try to find a 4-digit year in the option text
    const yearMatch = yearOption.text.match(/\d{4}/)
    if (yearMatch) {
      return parseInt(yearMatch[0])
    }
    return null
  }

  // Update the new name based on source attachment type, months, and year
  updateNewName() {
    const selectedOption = this.sourceAttachmentTypeTarget.selectedOptions[0]
    const sourceMonth = parseInt(this.sourceMonthTarget.value)
    const targetMonth = parseInt(this.targetMonthTarget.value)
    const targetYearOption = this.targetYearTarget.selectedOptions[0]

    if (!selectedOption || !selectedOption.value || !sourceMonth || !targetMonth || !targetYearOption) {
      return
    }

    const attachmentTypeName = selectedOption.text
    const sourceMonthName = this.getHebrewMonthName(sourceMonth)
    const targetMonthName = this.getHebrewMonthName(targetMonth)

    // Try to replace source month name with target month name in name
    let newName = attachmentTypeName

    // Check if name contains source month name
    if (sourceMonthName && newName.includes(sourceMonthName)) {
      newName = newName.replace(sourceMonthName, targetMonthName)
    } else if (targetMonthName) {
      // If source month not found, try to append target month
      newName = newName + " - " + targetMonthName
    }

    // Handle year replacement with academic year logic
    // Find all 4-digit years in the name
    const sourceYearMatches = attachmentTypeName.match(/\d{4}/g)
    if (sourceYearMatches && sourceYearMatches.length > 0) {
      // Get target academic start year from data attribute
      const targetAcademicStartYear = this.getAcademicStartYear(targetYearOption)

      if (targetAcademicStartYear) {
        // Replace year based on month logic
        sourceYearMatches.forEach(sourceYear => {
          // Determine what the target calendar year should be for the target month
          const targetCalendarYear = this.getCalendarYearForMonth(targetMonth, targetAcademicStartYear)

          // Replace source year with target year
          newName = newName.replace(sourceYear, targetCalendarYear.toString())
        })
      }
    }

    // Update the field
    this.newNameTarget.value = newName

    logger.log(`Updated name to: ${newName}`)
  }

  // Set default target year
  setDefaultYear() {
    if (!this.hasTargetYearTarget) return

    // Try to select the first year in the list as default
    if (this.targetYearTarget.options.length > 1) {
      this.targetYearTarget.selectedIndex = 1 // Skip the prompt
    }
  }
}
